import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInteractionStore } from "../src/stores/useInteraction";
import PlayerHealthHUD from "./PlayerHealthHUD";
import PlayerCoinsHUD from "./PlayerCoinsHUD";
import PlayerToyHUD from "./PlayerToyHUD";
import SkeletonShop from "./SkeletonShop";
import GunModeHUD from "./GunModeHUD";

export default function InteractionOverlay() {
  const promptVisible = useInteractionStore((state) => state.promptVisible);
  const promptText = useInteractionStore((state) => state.promptText);
  const hudMessage = useInteractionStore((state) => state.hudMessage);
  const shopVisible = useInteractionStore((state) => state.shopVisible);
  const [typedHudMessage, setTypedHudMessage] = useState("");
  const typingTimeoutRef = useRef(null);
  const typingIndexRef = useRef(0);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    if (typingTimeoutRef.current !== null) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    if (!hudMessage) {
      setTypedHudMessage("");
      typingIndexRef.current = 0;
      return;
    }

    typingIndexRef.current = 0;
    setTypedHudMessage("");

    const typeNext = () => {
      typingIndexRef.current += 1;
      setTypedHudMessage(hudMessage.slice(0, typingIndexRef.current));

      if (typingIndexRef.current < hudMessage.length) {
        typingTimeoutRef.current = window.setTimeout(typeNext, 35);
      } else {
        typingTimeoutRef.current = null;
      }
    };

    typingTimeoutRef.current = window.setTimeout(typeNext, 35);

    return () => {
      if (typingTimeoutRef.current !== null) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [hudMessage]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const detectTouch = () => {
      if (typeof navigator === "undefined") {
        return false;
      }
      return (
        "ontouchstart" in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
      );
    };

    setIsTouchDevice(detectTouch());

    const handleTouchStart = () => {
      setIsTouchDevice(true);
    };

    const handlePointerDown = (event) => {
      if (event.pointerType === "touch") {
        setIsTouchDevice(true);
      }
    };

    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("pointerdown", handlePointerDown);

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  const mobileButtonLabel = useMemo(() => {
    if (!promptText) return "Interactuar";
    const stripped = promptText
      .replace(/\s*\[[^\]]*\]\s*/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return stripped || "Interactuar";
  }, [promptText]);

  const handleMobileInteract = useCallback(() => {
    if (typeof window === "undefined") return;
    const event = new KeyboardEvent("keydown", {
      key: "e",
      code: "KeyE",
      bubbles: true,
    });
    window.dispatchEvent(event);
  }, []);

  const showMobileButton = isTouchDevice && promptVisible && !shopVisible;

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const className = "is-touch-device";
    const { body } = document;
    if (!body) return undefined;

    if (isTouchDevice) {
      body.classList.add(className);
    } else {
      body.classList.remove(className);
    }

    return () => {
      body.classList.remove(className);
    };
  }, [isTouchDevice]);

  return (
    <>
      <PlayerHealthHUD />
      <PlayerCoinsHUD />
      <PlayerToyHUD />
      <GunModeHUD />
      <SkeletonShop />
      {promptVisible && !isTouchDevice && (
        <div className="interaction-prompt">
          {promptText}
        </div>
      )}
      {showMobileButton && (
        <button
          type="button"
          className="mobile-interact-button"
          onClick={handleMobileInteract}
        >
          {mobileButtonLabel}
        </button>
      )}
      {typedHudMessage && (
        <div className="hud-message">
          {typedHudMessage.split("\n").map((line, index, array) => (
            <span key={index}>
              {line}
              {index < array.length - 1 && <br />}
            </span>
          ))}
        </div>
      )}
    </>
  );
}
